import React, { useEffect, useState, useCallback, useRef } from "react";
import styles from "./CategoryPage.module.scss";
import classNames from "classnames/bind";
import { useParams } from "react-router-dom";
import ProductItem from "../../components/ProductItem/ProductItem";
import {
  getProductsByCategories,
  getProductWithFilter,
} from "../../services/Products/ProductServices";
import useFilterStore from "../../stores/Filter/useFilterStore";
import useTranslations from "../../hooks/useTranslations";
import { translateProductName } from "../../utils/translateProductName";

const cx = classNames.bind(styles);
const UP = "ASC";
const DOWN = "DESC";

const CategoryPage = () => {
  const { category } = useParams();

  const productRef = useRef(null);
  const loadingRef = useRef(null); // Ref cho loading indicator

  const [productLists, setProductLists] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [filterPage, setFilterPage] = useState(1); // Thêm state riêng cho filter page
  const [pagination, setPagenation] = useState({});
  const [hasMore, setHasMore] = useState(true);

  // Dropdown states
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showGemstoneDropdown, setShowGemstoneDropdown] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedGemstone, setSelectedGemstone] = useState(null);
  const [selectedPopularity, setSelectedPopularity] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);

  const { gemstone, type } = useFilterStore();
  const currentTranslations = useTranslations();

  // Kiểm tra xem có filter nào được áp dụng không
  const hasActiveFilters =
    selectedType || selectedGemstone || selectedPopularity || selectedPrice;

  const fetchProducts = useCallback(
    async (pageNum, isLoadMore = false) => {
      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        const data = await getProductsByCategories(category, pageNum);

        if (data && data.products && Array.isArray(data.products)) {
          if (isLoadMore) {
            setProductLists((prev) => [...prev, ...data.products]);
          } else {
            setProductLists(data.products);
          }

          setPagenation(data.pagination);

          // Kiểm tra xem còn trang nào để load không
          if (data.pagination && pageNum >= data.pagination.total_pages) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setHasMore(false);
      }

      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    },
    [category]
  );

  // Sửa lại function này để hỗ trợ pagination với filter
  const fetchProductWithFilter = useCallback(
    async (pageNum = 1, isLoadMore = false) => {
      if (!hasActiveFilters) return;

      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      try {
        let orderBy = [];
        if (selectedPrice !== null) {
          orderBy.push({ key: "price", order: selectedPrice });
        }
        if (selectedPopularity) {
          orderBy.push({ key: "popularity", order: selectedPopularity });
        }

        // Thêm pageNum vào API call (giả sử API hỗ trợ pagination)
        const res = await getProductWithFilter(
          category,
          orderBy,
          selectedType?.slug || "",
          selectedGemstone?.slug || "",
          pageNum // Thêm page number
        );

        if (res.products) {
          if (isLoadMore) {
            setProductLists((prev) => [...prev, ...res.products]);
          } else {
            setProductLists(res.products);
          }

          setPagenation(res.pagination || {});

          // Kiểm tra xem còn trang nào để load không
          if (res && pageNum >= res.total_pages) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } else {
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching filtered products:", err);
        setHasMore(false);
      }

      if (isLoadMore) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    },
    [
      category,
      selectedType,
      selectedGemstone,
      selectedPopularity,
      selectedPrice,
      hasActiveFilters,
    ]
  );

  // Reset khi đổi category
  useEffect(() => {
    setPage(1);
    setFilterPage(1);
    setProductLists([]);
    setHasMore(true);
    setIsLoading(false);
    setIsLoadingMore(false);
  }, [category]);

  // Load products khi không có filter
  useEffect(() => {
    if (!hasActiveFilters) {
      fetchProducts(page, page > 1);
    }
  }, [page, fetchProducts, hasActiveFilters]);

  // Load products khi có filter - chỉ load trang đầu
  useEffect(() => {
    if (hasActiveFilters) {
      setFilterPage(1); // Reset filter page về 1
      fetchProductWithFilter(1, false);
    }
  }, [selectedType, selectedGemstone, selectedPopularity, selectedPrice]);

  // Intersection Observer cho infinite scroll
  useEffect(() => {
    if (!loadingRef.current || !hasMore || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          if (hasActiveFilters) {
            // Load more với filter - sử dụng filterPage
            const nextFilterPage = filterPage + 1;
            setFilterPage(nextFilterPage);
            fetchProductWithFilter(nextFilterPage, true);
          } else {
            // Load more không có filter
            setPage((prev) => prev + 1);
          }
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    observer.observe(loadingRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    hasMore,
    isLoading,
    isLoadingMore,
    hasActiveFilters,
    filterPage,
    fetchProductWithFilter,
  ]);

  const handleSelectedPopularity = () => {
    if (selectedPrice) {
      setSelectedPrice(null);
    }

    if (!selectedPopularity) {
      setSelectedPopularity(UP);
    } else if (selectedPopularity === UP) {
      setSelectedPopularity(DOWN);
    } else {
      setSelectedPopularity(null);
    }
  };

  const handleSelectedPrice = () => {
    if (selectedPopularity) {
      setSelectedPopularity(null);
    }

    if (!selectedPrice) {
      setSelectedPrice(UP);
    } else if (selectedPrice === UP) {
      setSelectedPrice(DOWN);
    } else {
      setSelectedPrice(null);
    }
  };

  const handleTypeSelect = (typeItem) => {
    setSelectedType(typeItem);
    setShowTypeDropdown(false);
  };

  const handleGemstoneSelect = (gemstoneItem) => {
    setSelectedGemstone(gemstoneItem);
    setShowGemstoneDropdown(false);
  };

  const clearFilters = () => {
    setSelectedType(null);
    setSelectedPopularity(null);
    setSelectedPrice(null);
    setSelectedGemstone(null);
    setPage(1);
    setFilterPage(1);
    setHasMore(true);
  };

  return (
    <div className={cx("container__category")}>
      <div className={cx("category__tittle")}>
        <h3 className={cx("tittle")}>
          {currentTranslations?.JewelryDropDown?.[category] ||
            currentTranslations?.GiftDropDown?.[category.split("-").join("_")]}
        </h3>
      </div>
      <div className={cx("category__list-products")}>
        <div className={cx("category__sortBy")}>
          {currentTranslations?.SortBy?.Sort_by} :
          <div className={cx("sortBy__item")}>
            <div
              onClick={handleSelectedPopularity}
              className={cx("dropdown-container")}
            >
              <p
                className={cx("dropdown-trigger", {
                  active: selectedPopularity,
                })}
              >
                {currentTranslations?.SortBy?.Popularity}

                {selectedPopularity !== null &&
                  ((selectedPopularity === UP && (
                    <span className={cx("dropdown-arrow")}>▲</span>
                  )) ||
                    (selectedPopularity === DOWN && (
                      <span className={cx("dropdown-arrow")}>▼</span>
                    )))}
              </p>
            </div>

            {/* Type Dropdown */}
            <div className={cx("dropdown-container")}>
              <p
                className={cx("dropdown-trigger", { active: selectedType })}
                onClick={() => {
                  setShowTypeDropdown(!showTypeDropdown);
                  setShowGemstoneDropdown(false);
                }}
              >
                {selectedType
                  ? selectedType.name
                  : currentTranslations?.SortBy?.Types}
              </p>
              {showTypeDropdown && (
                <div className={cx("dropdown-menu")}>
                  {type.map((typeItem) => (
                    <div
                      key={typeItem.id}
                      className={cx("dropdown-item", {
                        selected: selectedType?.id === typeItem.id,
                      })}
                      onClick={() => handleTypeSelect(typeItem)}
                    >
                      {typeItem.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Gemstone Dropdown */}
            <div className={cx("dropdown-container")}>
              <p
                className={cx("dropdown-trigger", { active: selectedGemstone })}
                onClick={() => {
                  setShowGemstoneDropdown(!showGemstoneDropdown);
                  setShowTypeDropdown(false);
                }}
              >
                {selectedGemstone
                  ? selectedGemstone.name
                  : currentTranslations?.SortBy?.Gemstone}
              </p>
              {showGemstoneDropdown && (
                <div className={cx("dropdown-menu")}>
                  {gemstone.map((gemstoneItem) => (
                    <div
                      key={gemstoneItem.id}
                      className={cx("dropdown-item", {
                        selected: selectedGemstone?.id === gemstoneItem.id,
                      })}
                      onClick={() => handleGemstoneSelect(gemstoneItem)}
                    >
                      {gemstoneItem.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              onClick={handleSelectedPrice}
              className={cx("dropdown-container")}
            >
              <p className={cx("dropdown-trigger", { active: selectedPrice })}>
                {currentTranslations?.SortBy?.Price}
                {selectedPrice !== null &&
                  ((selectedPrice === UP && (
                    <span className={cx("dropdown-arrow")}>▲</span>
                  )) ||
                    (selectedPrice === DOWN && (
                      <span className={cx("dropdown-arrow")}>▼</span>
                    )))}
              </p>
            </div>
          </div>
          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <button className={cx("clear-filters")} onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>

        {/* Đường line phân cách */}
        <div className={cx("category__separator")}></div>

        <div
          className={cx("list-products", {
            loading: isLoading && productLists.length === 0,
            "not-list":
              !isLoading && (!productLists || productLists.length === 0),
          })}
          ref={productRef}
        >
          {/* Hiển thị loading ban đầu */}
          {isLoading && productLists.length === 0 && (
            <div className={cx("loader")}></div>
          )}

          {/* Hiển thị danh sách sản phẩm */}
          {productLists &&
            productLists.length > 0 &&
            productLists.map((item, index) => {
              return (
                <ProductItem
                  href={`/product-detail/${item.id}/${item.slug}`}
                  key={`${item.id}-${index}`} // Cải thiện key
                  srcImage={item.image}
                  textType={"name"}
                  productText={translateProductName(item.name)}
                  price={item.sale_price ? item.regular_price : item.price}
                  price_sale={item.sale_price}
                  id={item.id}
                />
              );
            })}

          {/* Loading indicator cho infinite scroll */}
          {hasMore && productLists.length !== 0 && (
            <div ref={loadingRef} className={cx("loading-trigger")}>
              {isLoadingMore && <div className={cx("loader")}></div>}
            </div>
          )}
        </div>
        {!isLoading && (!productLists || productLists.length === 0) && (
          <div className={cx("no-products")}>
            <p>Không có sản phẩm nào trong danh mục này.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPage;
